import { Sparkles, Zap, Image, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * PremiumRecommendedModal - Display when user selects Fast mode but prompt requires Premium features
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} reason - Reason why Premium is recommended
 * @param {function} onSwitchToPremium - Callback when user chooses to switch to Premium
 * @param {function} onContinueWithFast - Callback when user wants to continue with Fast mode
 */
export function PremiumRecommendedModal({ 
  open, 
  onClose, 
  reason, 
  onSwitchToPremium, 
  onContinueWithFast 
}) {
  // Handle closing the modal - treat as "Continue with Fast"
  const handleClose = () => {
    onContinueWithFast();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border border-gray-700 text-white" data-testid="premium-recommended-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-6 w-6 text-yellow-400" />
            Premium Recommended
          </DialogTitle>
          <DialogDescription className="text-gray-300 pt-2">
            Your creative idea would look best with Premium features, but you can still proceed with Fast mode.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Reason */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-200 text-sm">
              {reason || "This prompt includes features that work best with Premium video generation."}
            </p>
          </div>
          
          {/* Comparison */}
          <div className="grid grid-cols-2 gap-3">
            {/* Fast Option */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Image className="h-4 w-4 text-blue-400" />
                <span className="font-medium text-sm">Fast Mode</span>
              </div>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Image with effects</li>
                <li>• Voiceover narration</li>
                <li>• Quick generation</li>
                <li>• Limited motion</li>
              </ul>
            </div>
            
            {/* Premium Option */}
            <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-purple-400" />
                <span className="font-medium text-sm text-purple-200">Premium</span>
              </div>
              <ul className="text-xs text-purple-200/80 space-y-1">
                <li>• Full video generation</li>
                <li>• Realistic lip-sync</li>
                <li>• Natural movement</li>
                <li>• Native audio</li>
              </ul>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onContinueWithFast}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            data-testid="continue-fast-btn"
          >
            <Image className="h-4 w-4 mr-2" />
            Proceed with Fast Anyway
          </Button>
          <Button
            onClick={onSwitchToPremium}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            data-testid="switch-premium-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Use Premium Instead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PremiumRecommendedModal;
